import { useState } from "react";
import { X, HeartCrack } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CHURN_REASONS, recordChurnReason } from "@/lib/churn";
import { cancelSubscriptionAtPeriodEnd } from "@/utils/payments.functions";
import { getPaymentEnvironment } from "@/lib/stripe";

interface Props {
  userId: string;
  planPriceId: string | null;
  onClose: () => void;
  onCanceled: () => void;
}

export function CancelSubscriptionDialog({ userId, planPriceId, onClose, onCanceled }: Props) {
  const [reason, setReason] = useState<string>("");
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!reason) {
      toast.error("Please pick a reason so we can improve.");
      return;
    }
    setSubmitting(true);
    try {
      await recordChurnReason({ userId, reason, details, priceId: planPriceId });
      const result = await cancelSubscriptionAtPeriodEnd({
        data: { environment: getPaymentEnvironment() },
      });
      if (!result?.ok) {
        toast.error("We saved your feedback, but couldn't cancel automatically. Use Manage Subscription.");
        return;
      }
      toast.success(
        result.periodEnd
          ? `Cancelled. You keep Pro until ${new Date(result.periodEnd).toLocaleDateString()}.`
          : "Cancelled. You keep Pro until the end of your billing period.",
      );
      onCanceled();
      onClose();
    } catch (err) {
      console.error("Cancellation failed:", err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60">
      <div className="modal-panel w-full max-w-md rounded-2xl border border-rule bg-card p-5 sm:p-6 space-y-4 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <HeartCrack className="size-5 text-neon-pink" />
            <h2 className="font-[family-name:var(--font-display)] font-bold text-lg text-ink">
              Before you go…
            </h2>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-ink-muted hover:text-ink">
            <X className="size-5" />
          </button>
        </div>
        <p className="text-sm text-ink-muted">
          What made you decide to cancel? Your answer directly shapes what we build next.
        </p>

        <div className="space-y-2">
          {CHURN_REASONS.map((r) => (
            <label
              key={r.value}
              className={`flex items-center gap-3 rounded-xl border px-3 py-2 text-sm cursor-pointer transition-colors ${
                reason === r.value ? "border-neon-pink bg-neon-pink/10 text-ink" : "border-rule text-ink-muted"
              }`}
            >
              <input
                type="radio"
                name="churn-reason"
                value={r.value}
                checked={reason === r.value}
                onChange={() => setReason(r.value)}
                className="accent-current"
              />
              {r.label}
            </label>
          ))}
        </div>

        <textarea
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          maxLength={1000}
          rows={3}
          placeholder="Anything else you'd like us to know? (optional)"
          className="w-full rounded-xl border border-rule bg-bg p-3 text-sm text-ink"
        />

        <div className="flex flex-col gap-2">
          <Button variant="subtle" size="full" onClick={onClose} disabled={submitting}>
            Keep my Pro plan
          </Button>
          <button
            onClick={submit}
            disabled={submitting}
            className="w-full py-2 text-sm font-semibold text-destructive underline disabled:opacity-60"
          >
            {submitting ? "Cancelling..." : "Confirm cancellation"}
          </button>
          <p className="text-[11px] text-ink-muted text-center">
            You'll keep Pro access until the end of your current billing period.
          </p>
        </div>
      </div>
    </div>
  );
}
